import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle, TrendingUp, MapPin, Trees, FileText } from "lucide-react";
import Navigation from "../components/layout/Navigation";
import Footer from "../components/layout/Footer";
import Breadcrumb from "../components/common/Breadcrumb";
import LeadForm from "../components/forms/LeadForm";

const EstimationForetPage = () => {
  useEffect(() => {
    document.title = "Estimation Forêt : Combien Vaut Votre Forêt ? | Sylva Invest";
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', "Propriétaire d'une forêt ou d'une parcelle boisée ? Demandez une première analyse de sa valeur et découvrez les solutions de vente et de valorisation possibles.");
    }
    let canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute('href', 'https://sylva-invest.com/estimation-foret');
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) ogTitle.setAttribute('content', 'Estimation Forêt : Combien Vaut Votre Forêt ? | Sylva Invest');
    const ogDesc = document.querySelector('meta[property="og:description"]');
    if (ogDesc) ogDesc.setAttribute('content', "Demandez une première analyse de la valeur de votre forêt et découvrez les solutions de valorisation possibles.");
    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) ogUrl.setAttribute('content', 'https://sylva-invest.com/estimation-foret');
    return () => {
      document.title = "Sylva Invest — Transactions Forestières, Remembrement & Conseil (France)";
      if (metaDescription) metaDescription.setAttribute('content', 'Sylva Invest accompagne propriétaires, héritiers et investisseurs pour recomposer le foncier forestier morcelé.');
      if (canonical) canonical.setAttribute('href', 'https://sylva-invest.com/');
    };
  }, []);

  const objectiveOptions = [
    { value: "valeur", label: "Je souhaite connaître la valeur de ma forêt" },
    { value: "vente_foncier", label: "Je souhaite vendre ma forêt ou ma parcelle" },
    { value: "vente_bois", label: "Je souhaite vendre ou valoriser les bois" },
    { value: "succession", label: "Je suis dans une succession ou une indivision" },
    { value: "regroupement", label: "Je souhaite acheter ou regrouper des parcelles voisines" },
    { value: "autre", label: "Autre" }
  ];

  const factors = [
    { icon: MapPin, title: "Localisation", text: "Région, département, accessibilité et attractivité du secteur" },
    { icon: Trees, title: "Peuplements", text: "Essences présentes, maturité des arbres et potentiel sylvicole" },
    { icon: FileText, title: "Configuration", text: "Surface, forme de la parcelle, nombre de lots et enclavement" },
    { icon: TrendingUp, title: "Potentiel", text: "Options de valorisation, regroupement possible, marché local" }
  ];

  return (
    <>
      <Navigation />
      <main className="pt-20">
        {/* Hero */}
        <section className="bg-forest text-white py-16 md:py-24">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <Breadcrumb items={[{ label: "Propriétaires" }, { label: "Estimation forêt" }]} />
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="max-w-4xl"
            >
              <p className="text-xs uppercase tracking-[0.2em] text-gold font-sans font-semibold mb-4">
                Analyse de Valeur
              </p>
              <h1 className="font-serif text-3xl md:text-4xl lg:text-5xl font-normal tracking-tight mb-6" data-testid="page-h1">
                Combien vaut votre forêt ?
              </h1>
              <p className="font-sans text-base md:text-lg text-white/80 leading-relaxed mb-8">
                La valeur d'une forêt dépend de nombreux facteurs : localisation, surface, accessibilité, configuration du foncier, essences présentes, maturité des peuplements et potentiel de valorisation. Sylva Invest vous propose une première analyse de votre actif forestier afin d'identifier les options qui peuvent être envisagées.
              </p>
              <a href="#formulaire" className="btn-primary inline-flex items-center">
                Faire estimer ma forêt
                <ArrowRight className="w-4 h-4 ml-2" />
              </a>
            </motion.div>
          </div>
        </section>

        {/* Factors */}
        <section className="py-16 md:py-20 bg-cream">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12"
            >
              <h2 className="font-serif text-2xl md:text-3xl text-forest mb-4">
                Les facteurs qui influencent la valeur d'une forêt
              </h2>
              <div className="w-12 h-px bg-gold mx-auto" />
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {factors.map((factor, index) => (
                <motion.div
                  key={factor.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-white p-6 border border-gray-100"
                >
                  <factor.icon className="w-8 h-8 text-gold mb-4" />
                  <h3 className="font-serif text-lg text-forest mb-2">{factor.title}</h3>
                  <p className="font-sans text-sm text-gray-600">{factor.text}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Important note */}
        <section className="py-12 bg-bone border-y border-gray-200">
          <div className="max-w-4xl mx-auto px-6 md:px-12">
            <div className="flex items-start">
              <CheckCircle className="w-6 h-6 text-gold mr-4 mt-1 flex-shrink-0" />
              <div className="font-sans text-gray-700">
                <p className="font-medium text-forest mb-2">Une première analyse, pas une expertise réglementaire</p>
                <p className="text-sm leading-relaxed">
                  Sylva Invest propose une pré-évaluation indicative et une analyse foncière de votre actif forestier. Cette première analyse permet d'identifier les grandes orientations possibles (conservation, vente, regroupement) et de vous orienter vers les professionnels adaptés si nécessaire. Elle ne constitue pas une expertise forestière certifiée au sens réglementaire.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Form */}
        <section id="formulaire" className="py-16 md:py-20 bg-cream scroll-mt-20">
          <div className="max-w-3xl mx-auto px-6 md:px-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-10"
            >
              <h2 className="font-serif text-2xl md:text-3xl text-forest mb-4">
                Faire estimer ma forêt
              </h2>
              <p className="font-sans text-gray-600">
                Complétez ce formulaire et nous vous recontacterons rapidement.
              </p>
            </motion.div>

            <LeadForm
              formType="estimation"
              title={null}
              submitButtonText="Envoyer ma demande"
              showSurface={true}
              showCadastre={true}
              showEssences={true}
              showAccessibility={true}
              showObjective={true}
              showTiming={true}
              objectiveOptions={objectiveOptions}
            />
          </div>
        </section>

        {/* Other options */}
        <section className="py-12 bg-forest text-white">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div>
                <p className="font-sans text-sm text-gold mb-2">Vous avez déjà un projet précis ?</p>
                <p className="font-serif text-xl">Découvrez nos accompagnements spécifiques</p>
              </div>
              <div className="flex flex-wrap gap-4">
                <Link to="/vendre-parcelle-bois" className="btn-primary bg-gold text-forest hover:bg-white hover:text-forest">
                  Vendre ma forêt
                </Link>
                <Link to="/vendre-bois-sur-pied" className="btn-primary bg-transparent border-2 border-white text-white hover:bg-white hover:text-forest">
                  Valoriser mes bois
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
};

export default EstimationForetPage;
